import { Goto } from "@/shared/navigation";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import { ApprovalMode } from "./approvalModes";
import {
  boardToolDeps,
  withDryRunContextAndBoardAccess,
} from "./boardAuthoringDeps";
import { ensureDryRunReport } from "./boardDryRunQuery";
import { blockedWriteError, dryRunOutcome } from "./boardDryRunResult";
import { TIME_RANGE_DESCRIPTION } from "./boardToolSchema";
import {
  type ClientToolDeps,
  type ClientToolExecutionResult,
  createClientTool,
  toolError,
  ToolErrorKind,
  toolSuccess,
} from "./ClientTools";
import { UpdateBoardConfirmation } from "./UpdateBoardConfirmation";
import {
  OPERATION_NAMES,
  toUpdateRequest,
  type UpdateBoardInput,
} from "./updateBoardInput";

async function execute(
  input: UpdateBoardInput,
  deps?: ClientToolDeps,
): Promise<ClientToolExecutionResult> {
  const parsed = toUpdateRequest(input);
  if ("problem" in parsed) {
    return toolError(parsed.problem, {}, ToolErrorKind.VALIDATION);
  }
  const { request } = parsed;
  const boardDeps = boardToolDeps(deps);
  const { goto, currentRoutePath } = boardDeps;
  return withDryRunContextAndBoardAccess(
    boardDeps,
    async (service, ctx, boards) => {
      // The dry run comes from the confirmation card's query when the card
      // ran it over the same patched and stored definitions; otherwise
      // `ensureDryRunReport` runs it once and caches it under the query key
      // built from those two definitions.
      const outcome = await service.update(request, ctx, boards, {
        dryRun: ensureDryRunReport,
      });
      switch (outcome.status) {
        case "refused": {
          // Each reason is sent once, in `reasons`; the error names the list
          // and does not repeat it.
          return toolError(
            "Nothing was changed, because this update could not be applied to " +
              "the board. `reasons` lists why, one entry per problem. " +
              "Fix every one and call again.",
            { reasons: outcome.reasons },
            ToolErrorKind.VALIDATION,
          );
        }
        case "save-refused": {
          return toolError(
            "The updated board passed its dry run, and the write was then " +
              "refused. `platform_reason` states why. Nothing was changed. " +
              "The same update sent " +
              "again is refused the same way, so change what that reason " +
              "names before calling again.",
            { platform_reason: outcome.reason },
            ToolErrorKind.VALIDATION,
          );
        }
        case "blocked": {
          return blockedWriteError(
            outcome.report,
            "The updated board did not pass its dry run, so nothing was changed.",
            "Fix all of them, a failing stage that no panel binds included, " +
              "and call again.",
          );
        }
        case "board-changed": {
          return toolError(
            `${outcome.message} Nothing was changed. Read the board again with ` +
              "client_read_board and apply the operations to what it returns.",
            {},
            ToolErrorKind.CONFLICT,
          );
        }
        case "board-deleted": {
          return toolError(
            `Board "${outcome.boardId}" was deleted while the dry run was ` +
              "running, so nothing was changed. List the boards by " +
              "calling client_read_board without board_id, or create one " +
              "with client_create_board.",
            { board_id: outcome.boardId },
            ToolErrorKind.NOT_FOUND,
          );
        }
        case "write-failed": {
          // The authoring service's message is the thrown error's own text,
          // which describes the network or the server and gives the model
          // nothing to act on, so it goes to error monitoring and not into the
          // result.
          ErrorMonitoringService.captureError(
            new Error(`Board update did not complete: ${outcome.message}`),
          );
          return toolError(
            "The updated board passed its dry run, but the request to write it " +
              "did not complete, so the board may or may not have changed. " +
              `Before calling again, read board "${request.boardId}" with ` +
              "client_read_board (detail 'full') and compare it with these " +
              "operations: if they are already applied, the write succeeded; " +
              "send them again only if they are not.",
            { board_id: request.boardId },
            ToolErrorKind.TRANSPORT,
          );
        }
        case "saved": {
          const route = Goto.createBoardRoute(outcome.boardId);
          const alreadyThere = currentRoutePath?.() === route;
          if (!alreadyThere) {
            goto.navigateTo(route);
          }
          return toolSuccess("updated_board", {
            board_id: outcome.boardId,
            title: outcome.title ?? null,
            ...dryRunOutcome(outcome.report),
            ...(alreadyThere ? {} : { navigated_to: route }),
          });
        }
      }
    },
  );
}

/**
 * `client_update_board`: builds or changes a board, named by id, with panel
 * and stage operations and whole replacements of its title, time range,
 * sections, selections and stages. No call creates a board.
 *
 * The authoring service reads the board's definition, applies the changes,
 * dry-runs the result, and writes it to the same board unless the dry run's
 * status is `blocked`. The write carries the revision that read returned, so
 * it is refused, and nothing is stored, if anyone replaced the definition or
 * deleted the board in between. Every outcome but `saved`
 * comes back as the tool error the model corrects from. The runner handles
 * approvals and claims each tool call before execution.
 *
 * The `add_stage` and `replace_stage` rules are stated in full in the guide's
 * `authoring-workflow` chapter ("The procedure"), and what a rename
 * rewrites, what a removal requires and when the whole list is the right
 * edit in its `updates-and-failures` chapter. The description and
 * schema here carry only what a caller needs to form a valid call, each rule
 * once.
 */
export const tool = createClientTool<UpdateBoardInput>({
  name: "client_update_board",
  description:
    "Builds or changes a board, an empty board included, in one call that " +
    "applies everything or nothing. Build and change the board " +
    "misc_context.requests_apply_to names with this tool. On " +
    "success it returns board_id, title and the dry-run outcome " +
    "client_create_board returns; otherwise `reasons` (the board is not " +
    "available to you, or the operations cannot be applied to its " +
    "definition), `platform_reason` (the write was refused) or the " +
    "dry-run report. `operations` edits panels by panel_id and stages by " +
    "stage_id, a control included, since a control is a stage; title, " +
    "time_range, calendar, sections, selections and stages each replace the " +
    "board's member whole. Before changing a board that has stages or " +
    "panels, read it with client_read_board: detail 'full' for stage " +
    "bodies, panel_id for one panel. The dry run checks the result of " +
    "all the operations together, " +
    "and every stage and panel must pass it, except a drawing fault the " +
    "saved board already has, which the report lists in " +
    'saved_board_faults (guide chapter "updates-and-failures", section ' +
    '"What a create or an update requires"). add_stage and replace_stage ' +
    'are in the guide chapter "authoring-workflow", section "The ' +
    'procedure"; rename_stage, remove_stage, what a blocked update ' +
    "returns, and the failures whose messages do not say all there is to " +
    'do are in the guide chapter "updates-and-failures". While ' +
    "misc_context.board.editing is present, the panel editor holds an " +
    "unsaved draft of the panel it names, which this tool does not " +
    "change; change it with client_update_panel_draft.",
  input_schema: {
    type: "object",
    required: ["board_id", "operations"],
    properties: {
      board_id: {
        type: "string",
        description:
          "The board to change, from misc_context.board or client_read_board.",
      },
      title: {
        type: "string",
        description: "The board's title after the update; omit to keep it.",
      },
      time_range: {
        description: `${TIME_RANGE_DESCRIPTION} Omit to keep the board's time range.`,
      },
      calendar: {
        type: "object",
        description:
          "The board's calendar after the update, as client_create_board's " +
          "calendar declares it: its time zone and week start. Omit to keep " +
          "the board's calendar.",
      },
      sections: {
        type: "array",
        items: { type: "object" },
        description:
          "The board's whole sections list as it should be after the " +
          "update, each item as client_create_board's sections declares " +
          "it. Send the list as read with one section more to create a " +
          "section, or with a member changed to change one; keep every " +
          "other section's members, its `selections` included. Omit to " +
          "keep the sections.",
      },
      operations: {
        type: "array",
        description:
          "The panel and stage edits, as a JSON array of operation objects " +
          "and not a string of JSON text, such as " +
          '[{"op":"add_stage","stage_id":"s1","stage":{...}},' +
          '{"op":"add","panel_id":"p1","panel":{...}}]. A panel id and a ' +
          "stage id may each appear in one operation only. May be empty " +
          "when only title, time_range, calendar, sections, selections or " +
          "stages change.",
        items: {
          type: "object",
          required: ["op"],
          properties: {
            op: {
              type: "string",
              enum: OPERATION_NAMES,
              description:
                "Send only the members the operation uses. Panels: " +
                "replace and add (panel_id, panel); remove (panel_id); " +
                "set_participation_and_publish (panel_id, and " +
                "participation or publish or both). Stages: add_stage " +
                "(stage_id, stage, after_stage_id?); replace_stage " +
                "(stage_id, stage); rename_stage (stage_id, name); " +
                "remove_stage (stage_id). Controls: " +
                "set_panels_filtered_by_control (control_id, panel_ids, " +
                'columns?), in the guide chapter "controls", section ' +
                '"Choosing the panels a control filters".',
            },
            panel_id: {
              type: "string",
              description: "The id of the panel the operation is on.",
            },
            panel: {
              type: "object",
              description:
                "The whole panel, as one item of client_create_board's " +
                "panels, with its id equal to panel_id. A replace that " +
                "omits placement keeps the panel's placement.",
            },
            stage_id: {
              type: "string",
              description:
                "The id of the stage the operation is on; for add_stage, " +
                "an id no stage on the board has.",
            },
            stage: {
              type: "object",
              description:
                "The whole stage {id, name, inputs?, body}, as one item " +
                "of client_create_board's stages, with id equal to " +
                "stage_id. On replace_stage, omitting inputs keeps the " +
                "inputs the stage has and sending inputs replaces them.",
            },
            name: {
              type: "string",
              description:
                "rename_stage only: the stage's name after the rename. Its " +
                "id does not change. add_stage takes the name inside stage.",
            },
            after_stage_id: {
              type: "string",
              description:
                "The id of a stage already on the board and not removed " +
                "in this call, or of a stage an earlier operation of this " +
                "call adds, to insert the added stage after. Omit to add " +
                "it at the end.",
            },
            participation: {
              description:
                "The panel's participation member as " +
                "client_create_board declares it; it replaces the panel's " +
                "one whole, null removes it, and omitting it keeps it.",
            },
            publish: {
              description:
                "The panel's publish member as client_create_board " +
                "declares it, with the same three cases.",
            },
            control_id: {
              type: "string",
              description:
                "The stage id of the value control whose control-filters " +
                "the operation sets, a control another operation of this " +
                "call adds included.",
            },
            panel_ids: {
              type: "array",
              items: { type: "string" },
              description:
                "Every panel the control filters after the call, not only " +
                "the ones to add: a panel left out stops being filtered, " +
                "and an empty list filters none.",
            },
            columns: {
              type: "array",
              items: {
                type: "object",
                required: ["panel_id", "stage_id"],
                properties: {
                  panel_id: { type: "string" },
                  stage_id: {
                    type: "string",
                    description: "A stage the panel binds.",
                  },
                  column: { type: "string" },
                  column_holds: {
                    type: "string",
                    enum: ["value", "entity-id"],
                  },
                  leave_unfiltered: { type: "boolean", enum: [true] },
                },
              },
              description:
                "Per panel and stage it binds: give column and " +
                "column_holds for a stage when a call without them is " +
                "refused because that panel's stage needs a column chosen; " +
                "the refusal lists the candidate columns. Or give " +
                "leave_unfiltered true to keep that stage unfiltered. " +
                "Otherwise omit columns.",
            },
          },
        },
      },
      selections: {
        type: "array",
        items: { type: "object" },
        description:
          "The board's whole selections list, each item as " +
          "client_create_board's selections declares it; it replaces " +
          "the board's list. In the same call, update every panel whose " +
          "interactions or selectionBindings name a selection that changed. " +
          "Omit to keep the list.",
      },
      stages: {
        type: "array",
        items: { type: "object" },
        description:
          "The board's whole stages list, replacing the one it has, each " +
          "item as client_create_board's stages declares it. Use it only " +
          "for what stage operations cannot address, such as a stage " +
          "with a missing or duplicate id, and never in a call that also " +
          "carries a stage operation. Send a JSON array, not a string of " +
          "JSON text. Omit to keep the list.",
      },
    },
  },
  execute,
  defaultApprovalMode: ApprovalMode.FOLLOW_AUTO,
  allowableApprovalModes: [
    ApprovalMode.REQUIRE_APPROVAL,
    ApprovalMode.FOLLOW_AUTO,
    ApprovalMode.AUTO_EXECUTE,
  ],
  displayName: "Update board",
  displayDescription:
    "Dry-runs proposed stage and panel changes to a board against live data, and applies them only if the dry run reports no failure.",
  ConfirmationContent: UpdateBoardConfirmation,
});
